/**
 * Панель качества данных: замечания среза с фильтрами.
 *
 * Фасеты считаются по всем замечаниям, а не по отфильтрованным: фильтр не
 * должен прятать сам факт существования других значений.
 *
 * Крупных чисел на экране нет намеренно (IA-04): «сколько всего, сколько
 * блокирующих, сколько открытых» — это ответ раздела «Что вошло и что нет», и
 * второй его показ здесь означал бы два места, где одно и то же число может
 * разойтись. Здесь начинается РАЗБОР: разрезы выборки, фильтры и сами строки.
 *
 * Замечания сгруппированы по коду и по умолчанию свёрнуты. До этого экран
 * выкладывал 154 строки одним списком высотой в двадцать восемь тысяч точек —
 * прочитать такое нельзя, можно только пролистать. Ответ, ради которого экран
 * открывают, — «каких замечаний сколько и насколько они тяжёлые»; сами строки
 * нужны потом и по одному коду за раз. Ничего не удалено: каждая строка
 * лежит внутри своей группы, а группа в свёрнутом виде называет свой объём.
 */

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api, type IssueOut } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { layout, PageHeader } from "../../components/AppShell";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import {
  Badge,
  Button,
  Card,
  DataStatePanel,
  InlineAlert,
  RequestErrorPanel,
} from "../../components/ui";
import { Disclosure } from "../../components/Disclosure";
import { Bar } from "../../components/Progress";
import { Select } from "../../components/Select";
import { StackedShare } from "../../components/StackedShare";
import {
  severityLabel,
  severityRank,
  statusLabel,
  subjectTypeLabel,
} from "../../lib/epistemic";
import { formatCount } from "../../lib/format";
import { defineMessages } from "../../i18n/define";
import { useNamespace, useT } from "../../i18n/hooks";
import { DENSITY, levelPath, preview } from "../../lib/levels";
import { share } from "../../lib/progress";
import {
  codeBars,
  groupIssues,
  severitySegments,
  statusSegments,
  type FacetBucket,
  type IssueGroup,
} from "./qualityModel";
import styles from "../features.module.css";

/**
 * Сколько строк замечаний забирается за один запрос.
 *
 * Число объявлено здесь, а не вписано в параметры: оно печатается на экране
 * ровно тогда, когда замечаний больше, и два места, где живёт один предел,
 * однажды разошлись бы — и человек прочитал бы «показаны все» там, где показана
 * первая страница.
 */
const PAGE_LIMIT = 500;

const m = defineMessages({
  loading: { id: "quality.state.loading", defaultMessage: "Загружаем замечания качества", description: "Заголовок экрана-заглушки, пока грузится список замечаний качества данных (менеджер, инспектор)" },
  loadFailed: { id: "quality.state.loadFailed", defaultMessage: "Замечания не загрузились", description: "Заголовок панели ошибки, когда список замечаний качества не пришёл с сервера" },
  retry: { id: "quality.state.retry", defaultMessage: "Повторить", description: "Кнопка на панели ошибки: повторить загрузку замечаний; коротко" },
  pageTitle: { id: "quality.page.title", defaultMessage: "Замечания качества", description: "Заголовок экрана разбора замечаний качества данных и последний шаг цепочки уровней над ним" },
  purpose: {
    id: "quality.page.purpose",
    defaultMessage:
      "Разбор замечаний качества: срез переносит их вместе с данными — открытое замечание не исчезает при импорте, а остаётся видимым и адресуемым до строки файла. Сколько их всего и сколько из них блокирующих — в разделе «Что вошло и что нет».",
    description: "Пояснение под заголовком экрана замечаний качества; «Что вошло и что нет» — название соседнего раздела (реестр среза), должно совпадать с ним",
  },
  toRegistry: { id: "quality.page.toRegistry", defaultMessage: "Что вошло и что нет", description: "Кнопка в шапке экрана замечаний: переход к разделу «Что вошло и что нет» (реестр среза); название раздела" },

  filtersAria: { id: "quality.filters.aria", defaultMessage: "Фильтры замечаний", description: "aria-label группы из трёх фильтров (тяжесть, статус, код) над списком замечаний" },
  severityLabel: { id: "quality.filters.severity.label", defaultMessage: "Тяжесть", description: "Подпись фильтра по тяжести замечания" },
  severityExplain: { id: "quality.filters.severity.explain", defaultMessage: "Тяжесть назначает правило проверки, а не разбирающий: блокирующее замечание останавливает публикацию записи независимо от согласия человека.", description: "Всплывающая подсказка к фильтру «Тяжесть» (менеджер, инспектор)" },
  severityAny: { id: "quality.filters.severity.any", defaultMessage: "Любая", description: "Пункт фильтра «Тяжесть»: без отбора; женский род (согласуется с «тяжесть»)" },
  statusLabel: { id: "quality.filters.status.label", defaultMessage: "Статус", description: "Подпись фильтра по статусу разбора замечания" },
  statusExplain: { id: "quality.filters.status.explain", defaultMessage: "Статус — судьба замечания, а не его тяжесть: «принятое ограничение» означает решение жить с дефектом, а не его устранение.", description: "Всплывающая подсказка к фильтру «Статус»; «принятое ограничение» — название одного из статусов" },
  statusAny: { id: "quality.filters.status.any", defaultMessage: "Любой", description: "Пункт фильтра «Статус»: без отбора; мужской род" },
  codeLabel: { id: "quality.filters.code.label", defaultMessage: "Код замечания", description: "Подпись фильтра по коду сработавшего правила проверки" },
  codeExplain: { id: "quality.filters.code.explain", defaultMessage: "Код — имя сработавшего правила. Какие правила сработали чаще всего, видно баром выше: список нужен, чтобы отобрать одно из них, а не чтобы их пересчитать.", description: "Всплывающая подсказка к фильтру «Код замечания»; «баром выше» — полосы «Какие правила сработали чаще всего»" },
  codeAny: { id: "quality.filters.code.any", defaultMessage: "Любой", description: "Пункт фильтра «Код замечания»: без отбора; мужской род" },
  reset: { id: "quality.filters.reset", defaultMessage: "Сбросить фильтры", description: "Кнопка справа от фильтров: снять все три отбора; коротко" },

  blockersTitle: { id: "quality.blockers.title", defaultMessage: "Блокирующих замечаний: {count}", description: "Заголовок плашки над списком; {count} — число блокирующих замечаний среза, уже отформатированное" },
  blockersBody: { id: "quality.blockers.body", defaultMessage: "Публикация записи по ним не выполняется — это остановка, а не оценка данных.", description: "Текст плашки о блокирующих замечаниях; «по ним» — по блокирующим замечаниям" },
  blockersFiltered: { id: "quality.blockers.filtered", defaultMessage: "Фильтр «Тяжесть» уже стоит на них: ниже только они.", description: "Продолжение плашки, когда фильтр «Тяжесть» уже выбран на «блокирующая»" },
  blockersOnly: { id: "quality.blockers.only", defaultMessage: "Оставить в списке только открытые блокирующие", description: "Ссылка в плашке: включить фильтры «блокирующая» и «открыто»" },

  listTitle: {
    id: "quality.list.title",
    defaultMessage:
      "Показано {shown} из {total, plural, one {# замечания} few {# замечаний} many {# замечаний} other {# замечаний}} в {groups, plural, one {# группе} few {# группах} many {# группах} other {# группах}}",
    description: "Заголовок карточки со списком замечаний; {shown} — сколько строк загружено, {total} — сколько их в срезе, {groups} — число групп по коду; склонение после «из» и «в»",
  },
  emptyTitle: { id: "quality.list.empty.title", defaultMessage: "Под фильтры ничего не подошло", description: "Заголовок пустого состояния списка замечаний при заданных фильтрах" },
  emptyBody: { id: "quality.list.empty.body", defaultMessage: "Замечания с такими значениями в срезе есть только в других фасетах — счётчики выше считаются по всему срезу.", description: "Пояснение под пустым списком: отбор пуст, а счётчики выше считаются по всему срезу; «фасет» — разрез выборки" },
  truncatedTitle: { id: "quality.list.truncated.title", defaultMessage: "Показана первая страница, а не весь срез", description: "Заголовок предупреждения: сервер отдал не все замечания" },
  truncatedBody: {
    id: "quality.list.truncated.body",
    defaultMessage:
      "Сервер отдаёт не больше {limit} строк за раз, а под текущий отбор попало {total}. Группы ниже и их тяжесть посчитаны по загруженным строкам: код, чьи замечания целиком остались за пределом страницы, в списке не появится. Разрезы выше считаются по всему срезу и обрезкой не задеты — сузьте отбор фильтрами, чтобы разбирать целиком.",
    description: "Текст предупреждения об обрезанной странице; {limit} и {total} — числа строк, уже отформатированные",
  },
  groupsNote: { id: "quality.list.groupsNote", defaultMessage: "Группы идут по тяжести, а не по количеству: один блокирующий важнее сорока примечаний. Тяжесть группы — худшая в ней, а не средняя. Нажатие раскрывает сами строки с адресом до листа и строки источника.", description: "Пояснение над списком групп замечаний: порядок и тяжесть групп, что раскрывает нажатие" },

  facetSeverityTitle: { id: "quality.facets.severity.title", defaultMessage: "По тяжести", description: "Заголовок 100%-полосы: замечания по тяжести" },
  facetStatusTitle: { id: "quality.facets.status.title", defaultMessage: "По статусу разбора", description: "Заголовок 100%-полосы: замечания по статусу разбора" },
  facetDenominator: { id: "quality.facets.denominator", defaultMessage: "замечаний среза", description: "Знаменатель полосы после числа: «N замечаний — замечаний среза»; родительный падеж" },
  facetNoun: { id: "quality.facets.noun", defaultMessage: "{count, plural, one {замечание} few {замечания} many {замечаний} other {замечания}}", description: "Счётное слово «замечание» без числа: из него строятся формы для подписи полосы (вызывается с 1, 2 и 5)" },
  facetSeverityCaption: { id: "quality.facets.severity.caption", defaultMessage: "Тяжесть назначает правило проверки, а не разбирающий: блокирующее замечание останавливает публикацию записи независимо от того, согласен ли с ним человек.", description: "Подпись под полосой «По тяжести»" },
  facetStatusCaption: { id: "quality.facets.status.caption", defaultMessage: "Статус — судьба замечания, а не его тяжесть: «принятое ограничение» означает решение человека жить с дефектом, а не его устранение.", description: "Подпись под полосой «По статусу разбора»; «принятое ограничение» — название одного из статусов" },
  facetAbsent: { id: "quality.facets.absent", defaultMessage: "В срезе нет ни одного замечания качества — считать не от чего.", description: "Вместо полосы, когда в срезе нет замечаний" },

  codesTitle: { id: "quality.codes.title", defaultMessage: "Какие правила сработали чаще всего", description: "Заголовок карточки с таблицей самых частых кодов правил" },
  codesCaption: { id: "quality.codes.caption", defaultMessage: "Порядок — по количеству: это единственный разрез замечаний, где вопрос и есть «чего больше». Длина полосы — доля от самого частого кода, а не от всех {total} замечаний среза, поэтому число стоит рядом с ней всегда.", description: "Подпись таблицы кодов (caption); {total} — число замечаний среза, уже отформатированное" },
  codesColCode: { id: "quality.codes.col.code", defaultMessage: "Код правила", description: "Заголовок колонки таблицы кодов: код правила проверки" },
  codesColFreq: { id: "quality.codes.col.freq", defaultMessage: "Как часто", description: "Заголовок колонки таблицы кодов: полоса частоты" },
  codesColCount: { id: "quality.codes.col.count", defaultMessage: "Замечаний", description: "Заголовок числовой колонки таблицы кодов: сколько замечаний; узкая колонка" },
  codesMore: {
    id: "quality.codes.more",
    defaultMessage:
      "Показаны {shown, plural, one {# код} few {# кода} many {# кодов} other {# кода}} из {all}; в остальных {hidden, plural, one {# замечании} few {# замечаниях} many {# замечаниях} other {# замечаниях}}. Остальные коды видны в разборе замечаний — в фильтре «Код замечания» и в группах строк.",
    description: "Примечание под таблицей кодов; {shown} — сколько кодов показано, {all} — всего кодов, {hidden} — сколько замечаний в непоказанных кодах; «Код замечания» — название фильтра",
  },

  groupSummary: {
    id: "quality.group.summary",
    defaultMessage:
      "{issues, plural, one {# замечание} few {# замечания} many {# замечаний} other {# замечания}} по {subjects, plural, one {# объекту} few {# объектам} many {# объектам} other {# объектам}} · {statuses}",
    description: "Свёрнутая сводка группы замечаний после бейджа тяжести; {issues} — число замечаний, {subjects} — число разных объектов, {statuses} — готовый перечень «открыто: 3, отложено: 2»",
  },
  groupStatusCount: { id: "quality.group.statusCount", defaultMessage: "{status}: {count}", description: "Элемент перечня статусов в сводке группы; {status} — название статуса строчными, {count} — число" },
  groupShow: { id: "quality.group.show", defaultMessage: "показать {count}", description: "Подсказка-действие справа от свёрнутой группы или остатка; {count} — сколько строк раскроется; коротко" },
  groupRestTitle: { id: "quality.group.rest.title", defaultMessage: "Остальные строки этой группы", description: "Заголовок вложенного раскрытия с остатком строк группы" },
  groupRestSummary: {
    id: "quality.group.rest.summary",
    defaultMessage: "ещё {count, plural, one {# замечание} few {# замечания} many {# замечаний} other {# замечания}} по тому же правилу — те же колонки и тот же порядок",
    description: "Сводка вложенного раскрытия; {count} — сколько строк группы не показано сразу",
  },

  colStatus: { id: "quality.table.col.status", defaultMessage: "Статус", description: "Заголовок колонки таблицы замечаний: статус разбора" },
  colSubject: { id: "quality.table.col.subject", defaultMessage: "Объект", description: "Заголовок колонки таблицы замечаний: объект, о котором замечание" },
  colKnown: { id: "quality.table.col.known", defaultMessage: "Что известно", description: "Заголовок колонки таблицы замечаний: текст замечания" },
  colSource: { id: "quality.table.col.source", defaultMessage: "Источник", description: "Заголовок колонки таблицы замечаний: лист и строка файла-источника" },
  sourceSheet: { id: "quality.table.source.sheet", defaultMessage: "лист «{sheet}»", description: "Ячейка «Источник»: лист файла; {sheet} — имя листа из файла" },
  sourceSheetRow: { id: "quality.table.source.sheetRow", defaultMessage: "лист «{sheet}», строка {row}", description: "Ячейка «Источник»: лист и строка файла; {sheet} — имя листа, {row} — номер строки" },
  subjectWithKey: { id: "quality.subject.withKey", defaultMessage: "{kind}: {key}", description: "Объект замечания: вид объекта и его ключ; {kind} — вид («Контур»), {key} — номер или ключ из данных" },
  subjectContourTitle: { id: "quality.subject.contourTitle", defaultMessage: "Контуры реестра с номером {number}", description: "Подсказка к ссылке на объект-контур: откроется список контуров с этим номером; {number} — номер контура" },
});

export function DataQuality() {
  useNamespace("quality");
  const t = useT();
  const context = useAnalysisContext();
  const [search, setSearch] = useSearchParams();

  const severity = search.get("severity") ?? "";
  const status = search.get("status") ?? "";
  const code = search.get("code") ?? "";

  const params = useMemo(
    () => ({
      ...context.params,
      limit: PAGE_LIMIT,
      ...(severity ? { severity } : {}),
      ...(status ? { status } : {}),
      ...(code ? { code } : {}),
    }),
    [context.params, severity, status, code],
  );

  const request = useRequest(() => api.issues(params), [
    context.key,
    severity,
    status,
    code,
  ]);

  const patch = (key: string, value: string) => {
    setSearch(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (!value) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  if (request.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (request.error && !request.data) {
    return (
      <RequestErrorPanel
        error={request.error}
        title={t(m.loadFailed)}
        action={<Button onClick={request.reload}>{t(m.retry)}</Button>}
      />
    );
  }
  if (!request.data) return null;

  const data = request.data;
  const total = data.by_severity.reduce((sum, bucket) => sum + bucket.count, 0);
  const rows = [...data.items].sort(
    (left, right) => severityRank(left.severity) - severityRank(right.severity),
  );
  const groups = groupIssues(rows);
  // Блокирующие считаются по ВСЕМУ срезу (фасет), а не по загруженной
  // странице: иначе фильтр прятал бы сам факт остановки публикации.
  const blockers =
    data.by_severity.find((bucket) => bucket.key === "blocker")?.count ?? 0;
  // Страница обрезана сервером: показанное меньше, чем есть. Это надо сказать
  // словами — «показано 500 из 1 240» без объяснения читается как «остальные
  // где-то рядом», а их здесь нет вовсе.
  const truncated = data.page.total > rows.length;

  return (
    <div className={styles.stack}>
      <PageHeader
        title={t(m.pageTitle)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.pageTitle)}
          />
        }
        purpose={t(m.purpose)}
        actions={
          <Link to={{ pathname: "/registry", search: context.linkSearch() }}>
            <Button>{t(m.toRegistry)}</Button>
          </Link>
        }
      />

      <div data-guide="quality-facets">
        <QualityFacets
          bySeverity={data.by_severity}
          byStatus={data.by_status}
          byCode={data.by_code}
          total={total}
        />
      </div>

      {/* Не `role="search"`: свободного поиска здесь нет и не было — три
          закрытых списка отбирают из уже загруженного среза. Поиск по имени
          живёт в шапке приложения, и второй ориентир с тем же именем сбивал бы
          с него. */}
      <div className={layout.filterBar} role="group" aria-label={t(m.filtersAria)} data-guide="quality-filters">
        {/* Три примитива вместо трёх нативных списков. Ради кода замечания это
            и делалось: `CONFLICT_RESOLUTION_ZERO_AREA_NOT_CARRIED` растягивал
            нативный `<select>` до своей длины и уводил всю страницу вбок. */}
        <Select
          label={t(m.severityLabel)}
          explain={t(m.severityExplain)}
          options={[
            { value: "", label: t(m.severityAny) },
            ...[...data.by_severity]
              .sort((a, b) => severityRank(a.key) - severityRank(b.key))
              .map((bucket) => ({
                value: bucket.key,
                label: severityLabel(bucket.key).label,
                hint: formatCount(bucket.count).text,
              })),
          ]}
          value={severity}
          onChange={(value) => patch("severity", value)}
        />

        <Select
          label={t(m.statusLabel)}
          explain={t(m.statusExplain)}
          options={[
            { value: "", label: t(m.statusAny) },
            ...data.by_status.map((bucket) => ({
              value: bucket.key,
              label: statusLabel(bucket.key).label,
              hint: formatCount(bucket.count).text,
            })),
          ]}
          value={status}
          onChange={(value) => patch("status", value)}
        />

        <Select
          label={t(m.codeLabel)}
          explain={t(m.codeExplain)}
          options={[
            { value: "", label: t(m.codeAny) },
            ...data.by_code.map((bucket) => ({
              value: bucket.key,
              label: bucket.key,
              hint: formatCount(bucket.count).text,
            })),
          ]}
          value={code}
          onChange={(value) => patch("code", value)}
        />

        {severity || status || code ? (
          <Button
            onClick={() =>
              setSearch(
                (previous) => {
                  const next = new URLSearchParams(previous);
                  next.delete("severity");
                  next.delete("status");
                  next.delete("code");
                  return next;
                },
                { replace: true },
              )
            }
          >
            {t(m.reset)}
          </Button>
        ) : null}
      </div>

      {/* Блокирующие — закреплённым блоком, а не строкой внутри разрезов
          (PG-DQ-02). Блокирующее замечание не «тяжёлое»: оно ОСТАНАВЛИВАЕТ
          публикацию записи, и это единственное состояние экрана, у которого
          есть последствие прямо сейчас. Плашка ведёт в фильтр, а не в текст:
          отсюда человек идёт разбирать, а не читать. */}
      {blockers > 0 ? (
        <InlineAlert
          title={t(m.blockersTitle, { count: formatCount(blockers).text })}
        >
          <p>
            {t(m.blockersBody)}{" "}
            {severity === "blocker" ? (
              <>{t(m.blockersFiltered)}</>
            ) : (
              <Link
                to={{
                  pathname: "/data-quality",
                  search: context.linkSearch({
                    severity: "blocker",
                    status: "open",
                  }),
                }}
              >
                {t(m.blockersOnly)}
              </Link>
            )}
          </p>
        </InlineAlert>
      ) : null}

      <div data-guide="quality-issues"><Card
        // Родительный после «из N»: 151 → «замечания», 2/5 → «замечаний».
        title={t(m.listTitle, {
          shown: rows.length,
          total: data.page.total,
          groups: groups.length,
        })}
        large
      >
        {rows.length === 0 ? (
          <DataStatePanel state="empty" title={t(m.emptyTitle)}>
            {t(m.emptyBody)}
          </DataStatePanel>
        ) : (
          <>
            {/* Честное поведение при обрезанной странице (PG-DQ-03). Группы и
                их тяжесть посчитаны по ЗАГРУЖЕННЫМ строкам: код, чьи замечания
                целиком лежат за пределом страницы, здесь не появится вовсе —
                и молчать об этом значило бы выдать первую страницу за срез. */}
            {truncated ? (
              <InlineAlert title={t(m.truncatedTitle)}>
                <p>
                  {t(m.truncatedBody, {
                    limit: formatCount(PAGE_LIMIT).text,
                    total: formatCount(data.page.total).text,
                  })}
                </p>
              </InlineAlert>
            ) : null}
            <p className={styles.demoBatch}>
              {t(m.groupsNote)}
            </p>
            {groups.map((group) => (
              <IssueGroupBlock group={group} key={group.code} />
            ))}
          </>
        )}
      </Card></div>
    </div>
  );
}

/**
 * Разрезы выборки замечаний — один блок на два места.
 *
 * До этого состав фасетов был виден только внутри выпадающих списков, то есть
 * читался лишь тем, кто уже собрался фильтровать. Полоса называет знаменатель
 * («замечаний среза») и не прячет ключи: сумма сверяется с ним самим
 * компонентом.
 *
 * Блок экспортируется, потому что тот же разрез показывает раздел «Что вошло и
 * что нет»: две копии сборки однажды разошлись бы порядком сегментов, и один и
 * тот же разрез читался бы на двух экранах по-разному.
 */
export function QualityFacets({
  bySeverity,
  byStatus,
  byCode,
  total,
}: {
  bySeverity: ReadonlyArray<FacetBucket>;
  byStatus: ReadonlyArray<FacetBucket>;
  byCode: ReadonlyArray<FacetBucket>;
  total: number;
}) {
  useNamespace("quality");
  const t = useT();
  const codes = codeBars(byCode, DENSITY.preview);
  // StackedShare склоняет по трём русским формам сам; формы берём из сообщения,
  // чтобы на другом языке слово не осталось русским (узбекское слово одно на все числа).
  const issueNoun: [string, string, string] = [
    t(m.facetNoun, { count: 1 }),
    t(m.facetNoun, { count: 2 }),
    t(m.facetNoun, { count: 5 }),
  ];

  return (
    <>
      <div className={`${layout.grid} ${layout.grid2}`}>
        <StackedShare
          title={t(m.facetSeverityTitle)}
          segments={severitySegments(bySeverity)}
          total={total}
          denominator={t(m.facetDenominator)}
          noun={issueNoun}
          caption={t(m.facetSeverityCaption)}
          absent={t(m.facetAbsent)}
        />
        <StackedShare
          title={t(m.facetStatusTitle)}
          segments={statusSegments(byStatus)}
          total={total}
          denominator={t(m.facetDenominator)}
          noun={issueNoun}
          caption={t(m.facetStatusCaption)}
          absent={t(m.facetAbsent)}
        />
      </div>

      {codes.shown.length > 0 ? (
        <Card title={t(m.codesTitle)}>
          <div className={styles.scrollX}>
            <table className={styles.plainTable}>
              <caption>
                {t(m.codesCaption, { total: formatCount(total).text })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t(m.codesColCode)}</th>
                  <th scope="col">{t(m.codesColFreq)}</th>
                  <th scope="col" className={styles.num}>
                    {t(m.codesColCount)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {codes.shown.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.key}</th>
                    <td>
                      <Bar value={share(row.count, codes.top)} />
                    </td>
                    <td className={styles.num}>{formatCount(row.count).text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {codes.hidden > 0 ? (
            <p className={styles.demoBatch}>
              {t(m.codesMore, {
                shown: codes.shown.length,
                all: codes.shown.length + codes.hidden,
                hidden: codes.hiddenCount,
              })}
            </p>
          ) : null}
        </Card>
      ) : null}
    </>
  );
}

/**
 * Одна группа замечаний. В свёрнутом виде она уже отвечает: сколько, какой
 * тяжести и в каких статусах. Раскрытие даёт адреса — то, что нужно уже при
 * разборе, а не при чтении.
 *
 * Внутри группы действует тот же бюджет уровня, что и везде:
 * `DENSITY.tablePreview` строк первым взглядом, остальные — вложенным
 * раскрытием той же таблицей. Группа на 154 строки раскрывалась в полотно, на
 * котором «разобрать одно» было неотличимо от «пролистать всё»; строки при
 * этом не удалены и названы числом.
 */
function IssueGroupBlock({ group }: { group: IssueGroup }) {
  const t = useT();
  const context = useAnalysisContext();
  const severity = severityLabel(group.severity);
  const head = preview(group.items, DENSITY.tablePreview);

  return (
    <div className={styles.issueGroup}>
      <Disclosure
        title={group.code}
        summary={
          <>
            <Badge tone={severity.known ? "attention" : "unknown"}>
              {severity.label}
            </Badge>{" "}
            {t(m.groupSummary, {
              issues: group.items.length,
              subjects: group.subjects,
              statuses: group.byStatus
                .map((bucket) =>
                  t(m.groupStatusCount, {
                    status: statusLabel(bucket.key).label.toLowerCase(),
                    count: bucket.count,
                  }),
                )
                .join(", "),
            })}
          </>
        }
        more={t(m.groupShow, { count: group.items.length })}
      >
        <IssueTable issues={head.shown} linkSearch={context.linkSearch} />

        {head.hidden > 0 ? (
          <Disclosure
            tone="inline"
            title={t(m.groupRestTitle)}
            summary={
              t(m.groupRestSummary, { count: head.hidden })
            }
            more={t(m.groupShow, { count: formatCount(head.hidden).text })}
          >
            <IssueTable
              issues={group.items.slice(head.shown.length)}
              linkSearch={context.linkSearch}
            />
          </Disclosure>
        ) : null}
      </Disclosure>
    </div>
  );
}

/** Строки замечаний одной группы. Колонки одни на первый взгляд и на остаток. */
function IssueTable({
  issues,
  linkSearch,
}: {
  issues: ReadonlyArray<IssueOut>;
  linkSearch: (extra?: Record<string, string | number>) => string;
}) {
  const t = useT();
  return (
    <div className={styles.scrollX}>
      <table className={styles.plainTable}>
        <thead>
          <tr>
            <th scope="col">{t(m.colStatus)}</th>
            <th scope="col">{t(m.colSubject)}</th>
            <th scope="col">{t(m.colKnown)}</th>
            <th scope="col">{t(m.colSource)}</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => {
            const statusInfo = statusLabel(issue.status);
            return (
              <tr key={issue.id}>
                <td>
                  <Badge tone={statusInfo.known ? "neutral" : "unknown"}>
                    {statusInfo.label}
                  </Badge>
                </td>
                <td>
                  <IssueSubject issue={issue} linkSearch={linkSearch} />
                </td>
                <td className={styles.issueMessage}>{issue.message}</td>
                <td>
                  {issue.sheet_name ? (
                    <>
                      {issue.source_row
                        ? t(m.sourceSheetRow, { sheet: issue.sheet_name, row: issue.source_row })
                        : t(m.sourceSheet, { sheet: issue.sheet_name })}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/**
 * Предмет замечания — вход на свой уровень, а не строка текста.
 *
 * Замечание всегда о чём-то конкретном, и до этой ссылки разбор шёл вручную:
 * человек читал номер контура и искал его в реестре сам. Переход закрывает
 * ровно этот разрыв.
 *
 * Чего он НЕ делает — не притворяется адресом карточки. Замечание адресует
 * контур его НОМЕРОМ (`subject_key`), а карточка живёт по идентификатору; в
 * выдаче замечаний соответствия «номер → id» нет, и придумывать его на клиенте
 * значит строить ссылку, которая иногда откроет чужой контур. Поэтому переход
 * ведёт на уровень контура с уже наложенным фильтром по номеру — адрес,
 * который точно означает то, что написано.
 *
 * Предметы без своего уровня (`f1_block` — блок исходной формы, `source_record`
 * — строка листа) ссылками не становятся: у них уровня нет, а вести «куда-
 * нибудь» хуже, чем не вести никуда. Их адрес раскрывается в колонке источника.
 */
function IssueSubject({
  issue,
  linkSearch,
}: {
  issue: IssueOut;
  linkSearch: (extra?: Record<string, string | number>) => string;
}) {
  const t = useT();
  // Вид предмета — по-русски, ключ — как в источнике. Русская подпись стоит
  // ВМЕСТО кода, а не рядом: при разборе спрашивают ключ, а не имя вида в базе.
  const subject = subjectTypeLabel(issue.subject_type);
  const label = issue.subject_key
    ? t(m.subjectWithKey, { kind: subject.label, key: issue.subject_key })
    : subject.label;
  const contourNo = contourNumberOf(issue);

  if (!contourNo) return <>{label}</>;

  return (
    <Link
      to={{
        pathname: levelPath("contour"),
        // Только контекст анализа плюс сам номер: срез, на котором замечание
        // прочитано, обязан доехать до уровня контура, иначе на той стороне
        // окажется другой мир.
        search: linkSearch({ q: contourNo }),
      }}
      title={t(m.subjectContourTitle, { number: contourNo })}
    >
      {label}
    </Link>
  );
}

/**
 * Номер контура из предмета замечания.
 *
 * `contour` адресует контур напрямую, `contour_area` — его категорию площади
 * («1866/ARABLE_IRRIGATED_POOR»): уровень у них один и тот же, контур.
 */
function contourNumberOf(issue: IssueOut): string | null {
  if (issue.subject_type !== "contour" && issue.subject_type !== "contour_area") {
    return null;
  }
  const number = issue.subject_key.split("/")[0]?.trim();
  // Номер контура — цифры. Всё прочее под этим типом (например, служебный
  // ключ вроде «conflicts-2026-08-17») контуром не является, и ссылка по нему
  // вела бы в пустой список, притворяясь адресом.
  return number && /^\d+$/.test(number) ? number : null;
}
